/**
 * PromotionTemplateEditorPage — Full template editor
 *
 * 4 sections: Template Info, Tier Levels, Perks, Addon Volume Pricing.
 * Plus a preview section and sticky save bar.
 *
 * Part of Plan 199, Epic 02.
 */

import { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, Trash2 } from "lucide-react";
import { PageContainer } from "@/components/layout/PageContainer";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  usePromotionTemplate,
  useSavePromotionTemplate,
  useDeletePromotionTemplate,
} from "./api/promotionTierHooks";
import {
  PromotionTierTable,
  areTiersValid,
  type TierRow,
} from "./components/PromotionTierTable";
import {
  PromotionPerkEditor,
  type PerkRow,
} from "./components/PromotionPerkEditor";
import {
  AddonVolumePricingEditor,
  type AddonVolumeRow,
} from "./components/AddonVolumePricingEditor";
import { TierPreviewSection } from "./components/TierPreviewSection";
import { ItemSearchInput } from "./components/ItemSearchInput";

export function PromotionTemplateEditorPage() {
  const { name: routeName } = useParams<{ name: string }>();
  const isNew = !routeName || routeName === "new";
  const templateName = isNew ? "" : routeName;
  const navigate = useNavigate();

  const { data: template, isLoading } = usePromotionTemplate(templateName);
  const saveMutation = useSavePromotionTemplate();
  const deleteMutation = useDeletePromotionTemplate();

  // ── Form State ──────────────────────────────────────────────
  const [name, setName] = useState("");
  const [enabled, setEnabled] = useState(true);
  const [appliesTo, setAppliesTo] = useState<"Item Code" | "Item Group">(
    "Item Code"
  );
  const [applyOnItem, setApplyOnItem] = useState("");
  const [applyOnItemGroup, setApplyOnItemGroup] = useState("");
  const [qtyCounting, setQtyCounting] = useState("Per Item");
  const [tiers, setTiers] = useState<TierRow[]>([createDefaultTier()]);
  const [perks, setPerks] = useState<PerkRow[]>([]);
  const [addonTiers, setAddonTiers] = useState<AddonVolumeRow[]>([]);
  const [basePrice, setBasePrice] = useState<number | null>(null);
  const [initialized, setInitialized] = useState(false);

  // ── Load existing template into form ────────────────────────
  useEffect(() => {
    if (template && !initialized) {
      setName(template.template_name || "");
      setEnabled(!!template.enabled);
      setAppliesTo(template.applies_to || "Item Code");
      setApplyOnItem(template.apply_on_item || "");
      setApplyOnItemGroup(template.apply_on_item_group || "");
      setQtyCounting(template.qty_counting_mode || "Per Item");
      setTiers(
        template.tier_levels.length > 0
          ? template.tier_levels.map((t) => ({
              localId: crypto.randomUUID(),
              min_qty: String(t.min_qty),
              max_qty: t.max_qty ? String(t.max_qty) : "",
              discount_percentage: String(t.discount_percentage),
              perk_names: t.perk_names || [],
            }))
          : [createDefaultTier()]
      );
      setPerks(
        template.perks.map((p) => ({
          localId: crypto.randomUUID(),
          perk_name: p.perk_name,
          perk_type: p.perk_type,
          linked_addon_field: p.linked_addon_field || "",
          description: p.description || "",
        }))
      );
      setAddonTiers(
        template.addon_volume_tiers.map((a) => ({
          localId: crypto.randomUUID(),
          addon_item: a.addon_item,
          min_qty: String(a.min_qty),
          max_qty: a.max_qty ? String(a.max_qty) : "",
          rate: String(a.rate),
        }))
      );
      setInitialized(true);
    }
  }, [template, initialized]);

  // ── Fetch base price when item changes ──────────────────────
  useEffect(() => {
    if (appliesTo === "Item Code" && applyOnItem) {
      fetchBasePrice(applyOnItem).then(setBasePrice);
    } else {
      setBasePrice(null);
    }
  }, [appliesTo, applyOnItem]);

  // ── Available perk names for tier assignment ────────────────
  const availablePerks = useMemo(
    () => perks.filter((p) => p.perk_name).map((p) => p.perk_name),
    [perks]
  );

  // ── Has Free Addon perk? (show addon volume pricing) ───────
  const hasFreeAddonPerk = useMemo(
    () => perks.some((p) => p.perk_type === "Free Addon"),
    [perks]
  );

  // ── Validation ──────────────────────────────────────────────
  const isValid = useMemo(() => {
    if (!name.trim()) return false;
    if (appliesTo === "Item Code" && !applyOnItem) return false;
    if (appliesTo === "Item Group" && !applyOnItemGroup) return false;
    if (!areTiersValid(tiers)) return false;
    return true;
  }, [name, appliesTo, applyOnItem, applyOnItemGroup, tiers]);

  // ── Save ────────────────────────────────────────────────────
  const handleSave = useCallback(() => {
    const data: Record<string, any> = {
      template_name: name.trim(),
      enabled: enabled ? 1 : 0,
      applies_to: appliesTo,
      apply_on_item: appliesTo === "Item Code" ? applyOnItem : null,
      apply_on_item_group:
        appliesTo === "Item Group" ? applyOnItemGroup : null,
      qty_counting_mode: qtyCounting,
      tier_levels: tiers.map((t) => ({
        min_qty: parseInt(t.min_qty),
        max_qty: t.max_qty ? parseInt(t.max_qty) : null,
        discount_percentage: parseFloat(t.discount_percentage),
        override_rate: null,
        perk_names: t.perk_names || [],
      })),
      perks: perks
        .filter((p) => p.perk_name.trim())
        .map((p) => ({
          perk_name: p.perk_name.trim(),
          perk_type: p.perk_type,
          linked_addon_field: p.linked_addon_field || "",
          description: p.description || "",
        })),
      addon_volume_tiers: addonTiers
        .filter((a) => a.addon_item && a.rate)
        .map((a) => ({
          addon_item: a.addon_item,
          min_qty: parseInt(a.min_qty) || 1,
          max_qty: a.max_qty ? parseInt(a.max_qty) : null,
          rate: parseFloat(a.rate),
        })),
    };

    if (!isNew && templateName) {
      data.name = templateName;
    }

    saveMutation.mutate(data, {
      onSuccess: (saved) => {
        if (isNew) {
          navigate(`/storefront/promotions/${saved.name}`, { replace: true });
        }
      },
    });
  }, [
    name, enabled, appliesTo, applyOnItem, applyOnItemGroup,
    qtyCounting, tiers, perks, addonTiers, isNew, templateName,
    saveMutation, navigate,
  ]);

  // ── Delete ──────────────────────────────────────────────────
  const handleDelete = useCallback(() => {
    if (!templateName) return;
    if (!window.confirm("Delete this promotion template?")) return;
    deleteMutation.mutate(templateName, {
      onSuccess: () => navigate("/storefront/promotions", { replace: true }),
    });
  }, [templateName, deleteMutation, navigate]);

  // ── Loading State ───────────────────────────────────────────
  if (!isNew && isLoading) {
    return (
      <PageContainer>
        <div className="p-6 space-y-4">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-64 rounded-2xl" />
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <div className="p-6 space-y-6 pb-24">
        {/* Header */}
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            className="rounded-full"
            onClick={() => navigate("/storefront/promotions")}
          >
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <h2 className="text-2xl font-semibold">
            {isNew ? "New Promotion Template" : name || "Edit Template"}
          </h2>
        </div>

        {/* Section 1: Template Info */}
        <SectionCard title="TEMPLATE INFO">
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-sm">Template Name</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Medical Apparel Bulk Deal"
                className="h-10"
              />
            </div>

            <div className="flex items-center justify-between">
              <Label className="text-sm">Enabled</Label>
              <Switch checked={enabled} onCheckedChange={setEnabled} />
            </div>

            <div className="space-y-1.5">
              <Label className="text-sm">Applies to</Label>
              <Select
                value={appliesTo}
                onValueChange={(v) =>
                  setAppliesTo(v as "Item Code" | "Item Group")
                }
              >
                <SelectTrigger className="h-10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Item Code">Specific Item</SelectItem>
                  <SelectItem value="Item Group">Item Group</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {appliesTo === "Item Code" ? (
              <div className="space-y-1.5">
                <Label className="text-sm">Item</Label>
                <ItemSearchInput
                  doctype="Item"
                  value={applyOnItem}
                  onChange={setApplyOnItem}
                  placeholder="Search items..."
                />
              </div>
            ) : (
              <div className="space-y-1.5">
                <Label className="text-sm">Item Group</Label>
                <ItemSearchInput
                  doctype="Item Group"
                  value={applyOnItemGroup}
                  onChange={setApplyOnItemGroup}
                  placeholder="Search item groups..."
                />
              </div>
            )}

            <div className="space-y-1.5">
              <Label className="text-sm">Qty Counting Mode</Label>
              <Select value={qtyCounting} onValueChange={setQtyCounting}>
                <SelectTrigger className="h-10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Per Item">Per Item</SelectItem>
                  <SelectItem value="Combined">
                    Combined across group
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[11px] text-muted-foreground">
                {qtyCounting === "Per Item"
                  ? "Each item's qty evaluated independently"
                  : "Sum all item qtys in group, apply highest qualifying tier"}
              </p>
            </div>
          </div>
        </SectionCard>

        {/* Section 2: Tier Levels */}
        <SectionCard title="TIER LEVELS">
          <PromotionTierTable
            tiers={tiers}
            availablePerks={availablePerks}
            onChange={setTiers}
          />
        </SectionCard>

        {/* Section 3: Perks */}
        <SectionCard title="PERKS">
          <PromotionPerkEditor perks={perks} onChange={setPerks} />
        </SectionCard>

        {/* Section 4: Addon Volume Pricing (only if Free Addon perk exists) */}
        {hasFreeAddonPerk && (
          <SectionCard title="ADDON VOLUME PRICING">
            <AddonVolumePricingEditor
              tiers={addonTiers}
              onChange={setAddonTiers}
            />
          </SectionCard>
        )}

        {/* Preview */}
        <SectionCard title="PREVIEW">
          <TierPreviewSection tiers={tiers} basePrice={basePrice} />
        </SectionCard>
      </div>

      {/* Sticky Save Bar */}
      <div className="fixed bottom-0 left-0 right-0 z-10 bg-background/95 backdrop-blur-sm border-t border-border px-6 py-3 flex items-center justify-between">
        <div>
          {!isNew && (
            <Button
              variant="ghost"
              size="sm"
              className="text-red-500 hover:text-red-600 hover:bg-red-50"
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
            >
              <Trash2 className="w-4 h-4 mr-1.5" />
              Delete
            </Button>
          )}
        </div>
        <Button
          onClick={handleSave}
          disabled={!isValid || saveMutation.isPending}
          size="sm"
        >
          {saveMutation.isPending && (
            <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
          )}
          {saveMutation.isPending ? "Saving..." : "Save Template"}
        </Button>
      </div>
    </PageContainer>
  );
}

// ── Helpers ───────────────────────────────────────────────────

function SectionCard({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-2">
      <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide pl-1">
        {title}
      </h3>
      <Card className="rounded-2xl border-0 shadow-apple-sm">
        <CardContent className="p-5">{children}</CardContent>
      </Card>
    </div>
  );
}

function createDefaultTier(): TierRow {
  return {
    localId: crypto.randomUUID(),
    min_qty: "1",
    max_qty: "",
    discount_percentage: "",
    perk_names: [],
  };
}

async function fetchBasePrice(itemCode: string): Promise<number | null> {
  try {
    const { frappeMethod } = await import("@/lib/erp/utils");
    const result = await frappeMethod<{ name: string; price_list_rate: number }[]>(
      "frappe.client.get_list",
      {
        doctype: "Item Price",
        filters: { item_code: itemCode, selling: 1 },
        fields: ["name", "price_list_rate"],
        limit_page_length: 1,
        order_by: "creation desc",
      }
    );
    if (result && result.length > 0) {
      return result[0].price_list_rate;
    }
    return null;
  } catch {
    return null;
  }
}
